import { Box, Grid, HStack, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { RankChangeIndicator } from '@/components';
import { ScoreReassignmentNoticeButton } from '@/components/ScoreReassignmentNoticeButton';
import { TournamentScoringHelpButton } from '@/components/TournamentScoringHelpButton';
import { isPendingLeagueRank, memberHasList } from '@/helpers/leagues/leagueMemberSync';
import type { leagueMember } from '@/models/user';
import { pageMetaTextProps } from '@/theme/sitePageTypography';
import { LeaguePageTitle } from './LeaguePageTitle';

function formatRankOfTotal(rank: number, total: number): ReactNode {
  if (total <= 0) {
    return rank.toLocaleString();
  }
  return (
    <>
      {rank.toLocaleString()}
      <Text as='span' color='gray.500' fontWeight='normal'>
        {' '}
        / {total.toLocaleString()}
      </Text>
    </>
  );
}

export function LeaguePageHeader(props: {
  leagueName: string;
  /** List-holding members shown on the leaderboard (same for global and private). */
  competingCount: number;
  menu?: ReactNode;
  userMemberData: leagueMember;
  userRankToDisplay: number;
  useActualRank: boolean;
  userListPosition: number;
  tournamentStarted: boolean;
}) {
  const {
    leagueName,
    competingCount,
    menu = null,
    userMemberData,
    userRankToDisplay,
    useActualRank,
    userListPosition,
    tournamentStarted,
  } = props;

  const hasList = memberHasList(userMemberData);
  const rankPending = hasList && tournamentStarted && isPendingLeagueRank(userMemberData.currentRank);
  const pointsText = !hasList
    ? null
    : tournamentStarted
      ? `${userMemberData.currentScore} pts`
      : '0 pts';
  const showScoringActions = hasList && tournamentStarted && !rankPending;

  const infoColumn = (
    <VStack align='center' spacing={2} w='full' minW={0}>
      <LeaguePageTitle leagueName={leagueName} />

      {!hasList ? (
        <Text {...pageMetaTextProps} textAlign='center'>
          No list yet
        </Text>
      ) : (
        <>
          <HStack spacing={2} align='center' justify='center' flexWrap='wrap' w='full'>
            <Text
              fontSize='md'
              fontWeight='semibold'
              lineHeight='short'
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {rankPending ? (
                'Rank pending'
              ) : (
                formatRankOfTotal(userRankToDisplay, competingCount)
              )}
            </Text>
            {useActualRank && !rankPending ? (
              <RankChangeIndicator
                memberData={userMemberData}
                getListPosition={() => userListPosition}
                fontSize='xs'
              />
            ) : null}
          </HStack>

          {pointsText ? (
            <HStack spacing={2} align='center' justify='center' flexWrap='wrap' w='full'>
              <Text {...pageMetaTextProps}>{pointsText}</Text>
              {showScoringActions ? (
                <>
                  <ScoreReassignmentNoticeButton size='xs' />
                  <TournamentScoringHelpButton />
                </>
              ) : null}
            </HStack>
          ) : null}
        </>
      )}
    </VStack>
  );

  if (!menu) {
    return infoColumn;
  }

  return (
    <Grid
      templateColumns={{ base: '1fr', lg: 'minmax(0, 1fr) auto' }}
      gap={2}
      alignItems='start'
      w='full'
    >
      {infoColumn}
      <Box display={{ base: 'none', lg: 'block' }} flexShrink={0}>
        {menu}
      </Box>
    </Grid>
  );
}
